import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { Bell, MessageCircle, Mail, Smartphone, RefreshCw, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CHANNEL_ICONS = { email: Mail, sms: Smartphone, whatsapp: MessageCircle };
const STATUS_COLORS = {
  sent: { ico: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  failed: { ico: XCircle, cls: "text-red-700 bg-red-50 border-red-200" },
  skipped: { ico: MinusCircle, cls: "text-zinc-500 bg-zinc-50 border-zinc-200" },
};

export default function Notifications() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [rules, setRules] = useState([]);
  const [resending, setResending] = useState(null);

  const refresh = () => {
    api.get("/notifications/feed").then((r) => setFeed(r.data));
    api.get("/notifications/rules").then((r) => setRules(r.data));
  };
  useEffect(() => { refresh(); }, []);

  const canResend = user && (user.role === "admin" || user.role === "command_center");

  const resend = async (id) => {
    setResending(id);
    try {
      const { data } = await api.post(`/notifications/${id}/resend`);
      toast.success(`Re-dispatched: ${data.status}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); } finally { setResending(null); }
  };

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Alerts & Escalations</p>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Notifications</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Email is live via <span className="font-mono">Resend</span>. SMS / WhatsApp via Twilio are <span className="font-mono bg-amber-100 text-amber-800 px-1 rounded">PENDING CREDENTIALS</span>. Recipients are resolved through <span className="underline">Admin → Contacts</span>.
        </p>
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid="resend-setup-banner">
          <span className="font-semibold">Resend setup note:</span> while the Resend account is in <span className="font-mono">test mode</span>, emails can only be delivered to the verified account email
          (<span className="font-mono">levis@brandonwheelz.com</span>). To dispatch alerts to ADO / Pod Lead / Command Center contacts, verify a sending domain at
          <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline ml-1">resend.com/domains</a> and update <span className="font-mono">SENDER_EMAIL</span> to use that domain (e.g. <span className="font-mono">ops@yourdomain.com</span>).
        </div>
      </div>

      {/* Rules */}
      <Card className="overflow-hidden" data-testid="rules-card">
        <div className="px-5 py-3 border-b border-zinc-200">
          <h3 className="font-display text-lg font-semibold">Escalation rules</h3>
          <p className="text-xs text-zinc-500">Warning at 24h, escalation at 48h. Risk threshold: daily target vs actual &lt; 70%.</p>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
            <tr>
              <th className="text-left py-2.5 px-4 font-semibold">Trigger</th>
              <th className="text-left py-2.5 px-4 font-semibold">Warning</th>
              <th className="text-left py-2.5 px-4 font-semibold">Escalation</th>
              <th className="text-left py-2.5 px-4 font-semibold">Threshold</th>
              <th className="text-left py-2.5 px-4 font-semibold">Recipients</th>
              <th className="text-left py-2.5 px-4 font-semibold">Channels</th>
              <th className="text-left py-2.5 px-4 font-semibold">Active</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-xs">No rules configured.</td></tr>}
            {rules.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`rule-row-${r.id}`}>
                <td className="py-2.5 px-4 capitalize font-medium">{r.trigger_type.replace("_", " ")}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{r.warning_after_hours}h</td>
                <td className="py-2.5 px-4 font-mono text-xs">{r.escalation_after_hours}h</td>
                <td className="py-2.5 px-4 font-mono text-xs">{r.threshold_percent}%</td>
                <td className="py-2.5 px-4 text-xs text-zinc-600">{r.recipients.join(", ")}</td>
                <td className="py-2.5 px-4">
                  <div className="flex gap-1.5">
                    {r.channels.map((ch) => {
                      const Icon = CHANNEL_ICONS[ch] || Bell;
                      return <span key={ch} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[10px] capitalize"><Icon className="h-3 w-3" />{ch}</span>;
                    })}
                  </div>
                </td>
                <td className="py-2.5 px-4">
                  {r.active_flag ? <StatusPill status="green">Active</StatusPill> : <StatusPill status="scheduled">Inactive</StatusPill>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Feed */}
      <Card className="overflow-hidden" data-testid="feed-card">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">Notification feed</h3>
            <p className="text-xs text-zinc-500">Generated by predictive risk and pending counts. Recipients resolved from Admin → Contacts.</p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="feed-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
        </div>
        <div className="divide-y divide-zinc-100 max-h-[600px] overflow-auto">
          {feed.length === 0 && <p className="p-6 text-sm text-zinc-500 text-center">No notifications yet.</p>}
          {feed.map((n) => {
            const overall = n.status || "stubbed";
            return (
              <div key={n.id} className="px-5 py-3 hover:bg-zinc-50" data-testid={`feed-row-${n.id}`}>
                <div className="flex items-start gap-3">
                  <Bell className={`h-4 w-4 mt-0.5 ${n.level === "escalation" ? "text-red-600" : "text-amber-600"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{n.campaign_name}</span>
                      <span className="font-mono text-[10px] text-zinc-500">{n.campaign_id}</span>
                      <StatusPill status={n.level === "escalation" ? "red" : "amber"}>{n.level}</StatusPill>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${STATUS_COLORS[overall]?.cls || "text-zinc-500 bg-zinc-50 border-zinc-200"}`} data-testid={`feed-status-${n.id}`}>
                        {(STATUS_COLORS[overall]?.ico || MinusCircle) && (() => {
                          const Ico = STATUS_COLORS[overall]?.ico || MinusCircle;
                          return <Ico className="h-3 w-3" />;
                        })()}
                        {overall}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-600 mt-1">{n.message}</p>

                    {/* Per-channel delivery details */}
                    {n.delivery_status && (
                      <div className="mt-2 space-y-1.5">
                        {Object.entries(n.delivery_status).map(([channel, info]) => {
                          const Icon = CHANNEL_ICONS[channel] || Bell;
                          const sc = STATUS_COLORS[info.status] || STATUS_COLORS.skipped;
                          return (
                            <div key={channel} className={`flex items-start gap-2 text-[11px] px-2 py-1 rounded border ${sc.cls}`} data-testid={`feed-${n.id}-${channel}`}>
                              <Icon className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="capitalize font-medium uppercase tracking-wider">{channel}</span>
                              <span>·</span>
                              <span className="font-medium">{info.status}</span>
                              {info.recipients && info.recipients.length > 0 && (
                                <>
                                  <span>·</span>
                                  <span className="font-mono text-zinc-700 break-all">{info.recipients.join(", ")}</span>
                                </>
                              )}
                              {info.status === "failed" && info.ref && (
                                <>
                                  <span>·</span>
                                  <span className="text-red-700 break-words">{String(info.ref).slice(0, 140)}</span>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Fallback recipient roles list */}
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-zinc-500">
                      <span>Roles: {n.recipients?.join(", ")}</span>
                      <span>·</span>
                      <span className="font-mono">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      {n.resent_at && <><span>·</span><span>resent {(n.resent_at || "").slice(0, 16).replace("T", " ")}</span></>}
                    </div>
                  </div>
                  {canResend && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resend(n.id)}
                      disabled={resending === n.id}
                      data-testid={`resend-${n.id}`}
                      className="flex-shrink-0"
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${resending === n.id ? "animate-spin" : ""}`} />
                      Resend
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
